/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import IconCheck from '../../assets/icons-v2/IconCheck';
import IconClose from '../../assets/icons-v2/IconClose';
import IconDelete from '../../assets/icons-v2/IconDelete';
import ButtonPrimary from '../../atoms/ButtonPrimary';
import ButtonSecondary from '../../atoms/ButtonSecondary';
import Section from '../../atoms/Section';
import { createLocation, deleteLocation, updateLocation } from '../../redux-actions/actionsLocations';
import LocationsUtil from '../../utils/LocationsUtil';
import FormPanel from '../atoms-v2/FormPanel';
import Advice from '../elements/Advice';
import BasicButtonGroup from '../elements/BasicButtonGroup';
import BasicInput from '../elements/BasicInput';
import FormCell from '../elements/FormCell';
import FormInfo from '../elements/FormInfo';
import FormLabel from '../elements/FormLabel';
import FormRow from '../elements/FormRow';
import ToggleBox from '../elements/ToggleBox';
import { useToastContext } from '../toast/ToastProvider';

const LocationEditor = ({
  model = {},
  isError,
  adviceText: initialAdviceText,
  showAdvice: hasAdvice,
  isUpdateMode,
  initModel,
  onCancel,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showToast } = useToastContext();

  const [rawModel, setRawModel] = useState(model);
  const [modelState, setModelState] = useState(model);

  const [showAdvice, setShowAdvice] = useState(hasAdvice || model.isModified || model.isNew);

  useEffect(() => {
    if (model !== rawModel) {
      if (model) {
        setModelState(model);
        if (model.isModified || model.isNew) {
          setShowAdvice(true);
        }
        setRawModel(model);
      } else {
        const emptyModel = LocationsUtil.getEmptyObject();
        setModelState(emptyModel);
        setRawModel(emptyModel);
      }
    }
  }, [model]);

  const handleChangeFormValue = (name, value) => {
    setModelState({
      ...modelState,
      [name]: value,
    });
  };

  const handleOnClickCreate = async () => {
    const strapiData = LocationsUtil.getAPIData(modelState);
    const newObject = await dispatch(createLocation(strapiData));
    onCreate(newObject);

    const emptyModel = initModel();
    setModelState(emptyModel);
    setRawModel(emptyModel);
  };

  const handleOnClickUpdate = async () => {
    const strapiData = LocationsUtil.getAPIData(modelState);
    await dispatch(updateLocation(model.id, strapiData));
    onUpdate();
  };

  const handleOnClickDelete = async () => {
    if (
      // eslint-disable-next-line no-alert
      window.confirm(t('location.prompts.delete', { description: modelState.description }))
    ) {
      const deletedObjects = await dispatch(deleteLocation(modelState.id));
      onDelete(deletedObjects);
      showToast(t('location.success.deleted', { description: modelState.description }), 'success');
    }
  };

  const handleOnClickAdvice = () => {
    setShowAdvice(false);
  };

  const { number, numberOld, description, descriptionOld, isModified, isNew } = modelState;

  let isSuccess = false;
  let isInfo = false;
  let adviceText = initialAdviceText;

  if (isModified) {
    isInfo = true;
    adviceText = t('location.info.deleted');
  }

  if (isNew) {
    isSuccess = true;
    adviceText = t('location.info.new');
  }

  const isDataValid = number && description;

  return (
    <FormPanel>
      <ToggleBox show={showAdvice}>
        <Section paddingBottom="S">
          <Advice
            text={adviceText}
            isSuccess={isSuccess}
            isInfo={isInfo}
            isError={isError}
            onClick={handleOnClickAdvice}
          />
        </Section>
      </ToggleBox>
      <Section paddingTop="S">
        <FormRow>
          <FormCell cols="4">
            <FormLabel text={`${t('common.fields.carrierNumber.long')} *`} />
            <BasicInput type="text" value={number} onChange={(value) => handleChangeFormValue('number', value)} />
            {numberOld && <FormInfo text={numberOld} />}
          </FormCell>
          <FormCell cols="8">
            <FormLabel text={`${t('location.descrtiption')} *`} />
            <BasicInput
              type="text"
              value={description}
              onChange={(value) => handleChangeFormValue('description', value)}
            />
            {descriptionOld && <FormInfo text={descriptionOld} />}
          </FormCell>
        </FormRow>
      </Section>
      <Section paddingTop="M">
        <FormRow>
          <FormCell cols="8">
            <BasicButtonGroup>
              {!isUpdateMode && (
                <ButtonPrimary
                  label={t('common.actions.create')}
                  icon={IconCheck}
                  disabled={!isDataValid}
                  onClick={handleOnClickCreate}
                />
              )}
              {isUpdateMode && (
                <ButtonPrimary
                  label={t('common.actions.apply')}
                  icon={IconCheck}
                  disabled={!isDataValid}
                  onClick={handleOnClickUpdate}
                />
              )}
              <ButtonSecondary label={t('common.actions.close')} icon={IconClose} onClick={onCancel} />
            </BasicButtonGroup>
          </FormCell>
          {isUpdateMode && (
            <FormCell cols="4" align="right">
              <ButtonSecondary label={t('location.actions.delete')} icon={IconDelete} onClick={handleOnClickDelete} />
            </FormCell>
          )}
        </FormRow>
      </Section>
    </FormPanel>
  );
};

LocationEditor.propTypes = {
  model: PropTypes.object,
  isUpdateMode: PropTypes.bool,
  showAdvice: PropTypes.bool,
  adviceText: PropTypes.string,
  isError: PropTypes.bool,
  initModel: PropTypes.func,
  onCancel: PropTypes.func,
  onCreate: PropTypes.func,
  onUpdate: PropTypes.func,
  onDelete: PropTypes.func,
};

LocationEditor.defaultProps = {
  model: null,
  isUpdateMode: false,
  showAdvice: false,
  adviceText: '',
  isError: false,
  initModel: () => {},
  onCancel: () => false,
  onCreate: () => false,
  onUpdate: () => false,
  onDelete: () => false,
};

export default LocationEditor;
